import { Box, Stack } from '@mui/material';
import { HomeDefault } from '@ucr/icons/main';
import { useMemo } from 'react';
import { useOutletContext } from 'react-router-dom';

import {
  MedOrgType,
  TMedOrgListItem,
  getMedOrgName,
  getMedOrgType,
  isIndividualEntrepreneur,
  isLegalEntity,
} from 'entities/MegOrgs';

import { formatDate } from 'shared/lib/utils/dates';
import { CardTemplate } from 'shared/ui/CardTemplate';
import { DottedTextRow } from 'shared/ui/DottedTextRow';
import { ExpandableList } from 'shared/ui/ExpandableList';
import { IconWrapper } from 'shared/ui/IconWrapper';
import { Loader } from 'shared/ui/Loader';
import { Tag } from 'shared/ui/Tag';
import { TagColor } from 'shared/ui/Tag/TagTypes.ts';
import { Text } from 'shared/ui/Text';

import cls from './MedOrgInfoContent.module.scss';

type KeyValue = { key: string; value: string };

const InfoBlock = ({ data }: { data: KeyValue[] }) => (
  <Stack gap='16px'>
    {data.map(({ key, value }) => (
      <DottedTextRow
        key={key}
        title={
          <Text variant='t2' color='var(--ucr-palette-text-grey-400)'>
            {key}
          </Text>
        }
        text={<Text variant='t2'>{value}</Text>}
      />
    ))}
  </Stack>
);

export const MedOrgInfoContent = () => {
  const { medOrg } = useOutletContext<{ medOrg: TMedOrgListItem }>();

  const displayData = useMemo(() => {
    if (!medOrg) return null;

    const type = getMedOrgType(medOrg);

    const info: KeyValue[] = [];

    if (type === MedOrgType.LegalEntity && isLegalEntity(medOrg)) {
      const { inn = '', ogrn, kpp = '' } = medOrg.info;
      info.push(
        { key: 'ИНН', value: inn },
        { key: 'ОГРН', value: ogrn },
        { key: 'КПП', value: kpp },
      );
    }

    if (
      type === MedOrgType.IndividualEntrepreneur &&
      isIndividualEntrepreneur(medOrg)
    ) {
      const { inn = '', ogrnip } = medOrg.info;
      info.push({ key: 'ИНН', value: inn }, { key: 'ОГРНИП', value: ogrnip });
    }

    const billing: KeyValue[] = [
      { key: 'Наименование банка', value: medOrg.billing?.bank ?? '' },
      { key: 'БИК', value: medOrg.billing?.bik ?? '' },
      { key: 'Расчётный счёт', value: medOrg.billing?.checkingAccount ?? '' },
      { key: 'Корр. счёт', value: medOrg.billing?.correspondentAccount ?? '' },
    ];

    const license: KeyValue[] = [
      {
        key: 'Серия/Номер',
        value:
          `${medOrg.license?.series ?? ''}/${medOrg.license?.number ?? ''}`.trim(),
      },
      {
        key: 'Действие',
        value: `с ${formatDate(medOrg.license?.issuedAt, 'DD.MM.YYYY') ?? '-'} по ${
          medOrg.license?.expiresAt
            ? formatDate(medOrg.license?.expiresAt, 'DD.MM.YYYY')
            : 'Бессрочно'
        }`,
      },
    ];

    return { info, billing, license };
  }, [medOrg]);

  if (!medOrg || !displayData) return <Loader />;

  const { info, billing, license } = displayData;
  const medOrgType = getMedOrgType(medOrg);

  const type = getMedOrgType(medOrg);

  const AvatarMods = {
    [cls.Avatar_color1]: type === MedOrgType.LegalEntity,
    [cls.Avatar_color2]: type === MedOrgType.IndividualEntrepreneur,
  };

  return (
    <Box className={cls.MedOrgInfoWrapper}>
      <CardTemplate title='Данные организации'>
        <Stack gap='21px'>
          <Stack direction='row' gap='16px'>
            <Stack className={cls.Avatar}>
              <IconWrapper icon={HomeDefault} className={cls.MedOrgInfoIcon} />
            </Stack>
            <Stack gap='10px'>
              <Tag color={TagColor.BLUE}>
                <Text variant='t3'>
                  {medOrgType === 'legalEntity'
                    ? 'Юридическое лицо'
                    : 'Индивидуальный предприниматель'}
                </Text>
              </Tag>
              <Text variant='t2' className={cls.MedOrgInfoText}>
                {getMedOrgName(medOrg)}
              </Text>
            </Stack>
          </Stack>
          <InfoBlock data={info} />
        </Stack>
      </CardTemplate>

      <CardTemplate title='Банковские реквизиты'>
        <InfoBlock data={billing} />
      </CardTemplate>

      <CardTemplate title='Лицензия на медицинскую деятельность'>
        <InfoBlock data={license} />
      </CardTemplate>

      <CardTemplate title='Контактные лица'>
        <ExpandableList
          itemsLength={medOrg.contacts.length}
          initialItemsCount={1}
          maxExpandedHeight={300}
          buttonTextReveal='Все контакты'
          buttonTextHide='Свернуть контакты'
          gap='16px'
        >
          {medOrg.contacts.map((item, index) => (
            <Stack
              key={item.id}
              gap='16px'
              paddingTop='16px'
              borderTop={
                index === 0
                  ? 'none'
                  : '1px solid var(--ucr-palette-line-grey-150)'
              }
            >
              <Stack gap='2px'>
                {item.position && (
                  <Text variant='t2' color='var(--ucr-palette-text-grey-400)'>
                    {item.position}
                  </Text>
                )}
                <Text variant='h6'>
                  {`${item.fullname.surname} ${item.fullname.name} ${item.fullname.patronymic || ''}`}
                </Text>
              </Stack>

              {item.phone && (
                <DottedTextRow
                  title={
                    <Text variant='t2' color='var(--ucr-palette-text-grey-400)'>
                      Телефон
                    </Text>
                  }
                  text={<Text variant='t2'>{item.phone}</Text>}
                />
              )}

              <DottedTextRow
                title={
                  <Text variant='t2' color='var(--ucr-palette-text-grey-400)'>
                    E-mail
                  </Text>
                }
                text={<Text variant='t2'>{item.email}</Text>}
              />
            </Stack>
          ))}
        </ExpandableList>
      </CardTemplate>
    </Box>
  );
};
